import { useState } from "react";
import { X } from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import type { ExternalAttendeeDto } from "@/api/appointments";
import { APPOINTMENT_ZONES } from "@/lib/appointmentZones";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  value: ExternalAttendeeDto[];
  onChange: (next: ExternalAttendeeDto[]) => void;
}

/**
 * Calendar AI Create+ — editor for external (non-staff) attendees. Each chip is
 * visually distinct from the internal-user chips (a neutral surface + an
 * "External" badge) so it's obvious who receives an email rather than an in-app
 * invite. Email is required and format-validated before a chip is added.
 */
export function ExternalAttendeeEditor({ value, onChange }: Props) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const e = email.trim();
    if (!e) {
      setError("Enter an email address.");
      return;
    }
    if (!EMAIL_RE.test(e)) {
      setError("Enter a valid email address.");
      return;
    }
    if (value.some((a) => a.email.toLowerCase() === e.toLowerCase())) {
      setError("That email is already added.");
      return;
    }
    onChange([...value, { name: name.trim(), email: e, notes: notes.trim() || null }]);
    setEmail("");
    setName("");
    setNotes("");
    setError(null);
  };

  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  // "" is an explicit clear, not an omission — the server reads absent as
  // "leave whatever is stored alone", which is what protects a zone from being
  // wiped by an unrelated edit.
  const setZone = (idx: number, tz: string) =>
    onChange(value.map((a, i) => (i === idx ? { ...a, timeZone: tz } : a)));

  const onAddKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className="flex flex-col gap-space-2">
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-space-1">
          {value.map((a, i) => (
            <span
              key={`${a.email}-${i}`}
              className="inline-flex items-center gap-space-1 rounded-full border border-border-light bg-white-warm px-space-2 py-[2px] text-caption font-display text-onyx"
            >
              <span className="text-[10px] uppercase tracking-wide text-gray-mid">
                External
              </span>
              <span className="max-w-[12rem] truncate">
                {a.name?.trim() ? a.name : a.email}
              </span>
              {/*
                Per-attendee rather than on the add row, so it works the same for
                someone invited long before this field existed as for someone
                being added now — which is the common case, since every existing
                appointment has attendees and none of them has a zone.

                Labelled by ZONE NAME, never by IANA id: a staffer must be able to
                find "Eastern Time" for an attendee in Farmington Hills without
                knowing that Michigan is America/Detroit.
              */}
              <select
                value={a.timeZone ?? ""}
                aria-label={`Time zone for ${a.email}`}
                title="Their time zone (optional — only set this if you know it)"
                onChange={(e) => setZone(i, e.target.value)}
                className="bg-transparent text-[10px] text-gray-mid focus:outline-none focus-visible:ring-1 focus-visible:ring-gold rounded-sm"
              >
                <option value="">Zone not known</option>
                {APPOINTMENT_ZONES.map((z) => (
                  <option key={z.ianaId} value={z.ianaId}>
                    {z.pickerLabel}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${a.email}`}
                className="text-gray-mid hover:text-status-error"
              >
                <X size={12} strokeWidth={2} aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-space-2 sm:grid-cols-3">
        <Input
          type="email"
          value={email}
          placeholder="email@company.com"
          aria-label="External attendee email"
          invalid={Boolean(error)}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={onAddKey}
        />
        <Input
          type="text"
          value={name}
          placeholder="Name (optional)"
          aria-label="External attendee name"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onAddKey}
        />
        <div className="flex gap-space-2">
          <Input
            type="text"
            value={notes}
            placeholder="Note to them (optional)"
            aria-label="External attendee note"
            className="flex-1"
            onChange={(e) => setNotes(e.target.value)}
            onKeyDown={onAddKey}
          />
          <Button type="button" variant="secondary" size="sm" onClick={add}>
            Add
          </Button>
        </div>
      </div>
      {error && (
        <p className="m-0 text-label text-status-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
